import {
  ArrowLeft,
  Copy,
  Edit,
  Plus,
  Package,
  UserPlus,
  CalendarDays,
  Sparkles,
} from "lucide-react";
import { FC, useState } from "react";
import { useDetailBookingStore, BookingTab } from "./detail-booking.store";
import { BookingOzDrawer } from "./booking-oz-drawer";
import { getStatusBadge } from "./booking-utils";
import { useNavigate } from "react-router-dom";
import { useBooking } from "~/store";
import { useClientStore } from "~/components/admin/client-management/clients/clients.store";
import { PrintWaybill } from "~/components/admin/dispatch/print-labels/print-waybill";
import { TutorialTip } from "./tutorial-tip";

type Props = {};

export const HeaderBooking: FC<Props> = () => {
  const [ozOpen, setOzOpen] = useState(false);
  const navigate = useNavigate();
  const { openBookingModal } = useBooking();
  const { openClientModal } = useClientStore();
  const { booking, setActiveTab, setShowServiceForm, setShowInventoryModal, setIsAddingNewService, setShowExpoModal } = useDetailBookingStore();

  const isQuickClient = !booking?.client && !!booking?.tempClientData;
  const isAppBooking = booking?.serviceTicket?.some((ticket: any) => 
    ticket.staffNotes && ticket.staffNotes.includes("=== CLIENT SERVICE REQUEST ===")
  );

  const handleEdit = () => {
    openBookingModal({
      bookingId: booking?.id,
    });
  };

  const handleDuplicate = () => {
    navigate(`/bookings/copy/${booking?.id}`);
  };

  const allWaybillServices = (booking?.serviceTicket || []).map((ticket: any) => ({
    ...ticket,
    booking,
    clientService: ticket.client,
  }));

  const handleBack = () => {
    navigate(-1);
  };

  const handleAddService = () => {
    setActiveTab(BookingTab.SERVICES);
    setIsAddingNewService(true);
    setShowServiceForm(true);
  };

  const handleViewInventory = () => {
    setShowInventoryModal(true);
  };

  const handleConvertToClient = () => {
    if (booking?.tempClientData && booking?.id) {
      openClientModal(undefined, {
        prefillData: booking.tempClientData,
        bookingIdToLink: booking.id,
      });
    }
  };

  return (
    <div className="mb-6 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
      <div className="flex items-center space-x-3">
        <button
          onClick={handleBack}
          className="p-2 hover:bg-white rounded-full border"
        >
          <ArrowLeft size={16} />
        </button>
        <div>
          <h1 className="text-2xl font-semibold flex items-center">
            Booking Details: {booking?.referenceNumber}
            <span className="ml-3">
              {booking?.status && getStatusBadge(booking?.status)}
            </span>
          </h1>
          <div className="h-2" /> {/* Visual space between elements */}
          <p className="text-2xl font-semibold flex items-center">
            {booking?.client?.companyName ||
              [booking?.client?.firstName, booking?.client?.lastName]
                .filter(Boolean)
                .join(" ")
                .trim() ||
              booking?.tempClientData?.name ||
              "Unnamed Client"}{" "}
            {booking?.tempClientData && !booking?.client && (
              <span className="ml-2 text-sm bg-orange-100 text-orange-700 px-2 py-0.5 rounded">New Lead</span>
            )}
            {isAppBooking && (
              <span className="ml-2 text-sm bg-orange-100 text-orange-700 px-2 py-0.5 rounded">APP BOOKING</span>
            )}
            {booking?.project?.name && ` • ${booking.project.name}`}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {isQuickClient && (
          <TutorialTip text="Convert this quick lead into a full client profile in the system">
            <button
              onClick={handleConvertToClient}
              className="px-3 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center space-x-1"
            >
              <UserPlus size={16} />
              <span>Convert to Client</span>
            </button>
          </TutorialTip>
        )}
        {booking?.project && (
          <TutorialTip text="Switch to Expo Mode to manage multi-day events with setup, show days, and strike">
            <button
              onClick={() => setShowExpoModal(true)}
              className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center space-x-1"
            >
              <CalendarDays size={16} />
              <span>Expo Mode</span>
            </button>
          </TutorialTip>
        )}
        <TutorialTip text="Add a new service ticket (collection, delivery, on-site, warehouse, or fabrication) to this booking">
          <button
            onClick={handleAddService}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-1"
          >
            <Plus size={16} />
            <span>Add Service</span>
          </button>
        </TutorialTip>
        <TutorialTip text="Quick view of all inventory under the client's control">
          <button
            onClick={handleViewInventory}
            className="px-3 py-2 border rounded-lg hover:bg-white flex items-center space-x-1"
          >
            <Package size={16} />
            <span>View Inventory</span>
          </button>
        </TutorialTip>
        <TutorialTip text="Edit the booking's client, project, sidemark, or override the booking status">
          <button
            onClick={handleEdit}
            className="px-3 py-2 border rounded-lg hover:bg-white flex items-center space-x-1"
          >
            <Edit size={16} />
            <span>Edit Booking Detail</span>
          </button>
        </TutorialTip>
        <TutorialTip text="Create a brand new client in the system">
          <button
            onClick={() => openClientModal()}
            className="p-2 border rounded-lg hover:bg-white flex items-center justify-center"
            title="New Client"
          >
            <UserPlus size={16} />
          </button>
        </TutorialTip>
        <TutorialTip text="Duplicate this booking — choose which services, dates, and vehicles to copy">
          <button
            onClick={handleDuplicate}
            className="p-2 border rounded-lg hover:bg-white flex items-center justify-center"
            title="Duplicate"
          >
            <Copy size={16} />
          </button>
        </TutorialTip>
        <PrintWaybill
          size="small"
          selectedServiceTicketsForPrint={allWaybillServices}
          all
          iconOnly
          className="!p-2 !h-auto !w-auto border rounded-lg hover:bg-white flex items-center justify-center !shadow-none"
        />
        <button
          onClick={() => setOzOpen(true)}
          className="px-3 py-2 bg-black text-white rounded-lg hover:bg-gray-800 flex items-center space-x-1"
        >
          <Sparkles size={16} />
          <span>Ask Oz</span>
        </button>
      </div>

      <BookingOzDrawer
        open={ozOpen}
        onClose={() => setOzOpen(false)}
        booking={booking}
      />
    </div>
  );
};
